import { useState, useEffect, useCallback } from "react";
import { fmtKst } from "../lib/format";
import { PlayCircle, RefreshCw, Landmark, ShieldAlert, BarChart3 } from "lucide-react";
import {
  fetchInvestOverview,
  fetchInvestHoldings,
  fetchInvestRebalances,
  simulateInvestRebalance,
} from "../api/client";
import type {
  InvestOverview,
  InvestHolding,
  InvestRebalanceRow,
  InvestSimulateResponse,
} from "../api/types";

const ROLE_LABELS: Record<string, string> = {
  SPY: "미국 시장 코어 (대형주)",
  QQQ: "글로벌 빅테크 (성장)",
  SMH: "AI/반도체 주도주",
  TLT: "미국 20년+ 장기국채",
  GLD: "금 (지정학/인플레 헤지)",
  SGOV: "미국 초단기채 (현금 파킹)",
};

function fmtKrw(v: number | null | undefined): string {
  if (v === null || v === undefined || isNaN(Number(v))) return "—";
  return `₩${Number(v).toLocaleString("ko-KR", { maximumFractionDigits: 0 })}`;
}

function fmtUsd(v: number | null | undefined): string {
  if (v === null || v === undefined || isNaN(Number(v))) return "—";
  return `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || isNaN(Number(v))) return "—";
  return `${(Number(v) * 100).toFixed(digits)}%`;
}

function fmtQty(v: number | null | undefined): string {
  if (v === null || v === undefined || isNaN(Number(v))) return "—";
  return Number(v).toFixed(2);
}

function driftBadge(drift: number | null) {
  if (drift == null) return <span className="badge badge-yellow">평가 근거 미확인</span>;
  const abs = Math.abs(drift);
  if (abs < 0.03) return <span className="badge badge-green">정상</span>;
  if (abs < 0.06) return <span className="badge badge-yellow">이격 주의</span>;
  return <span className="badge badge-red">리밸런스 필요</span>;
}

export default function Invest() {
  const [overview, setOverview] = useState<InvestOverview | null>(null);
  const [holdings, setHoldings] = useState<InvestHolding[]>([]);
  const [rebalances, setRebalances] = useState<InvestRebalanceRow[]>([]);
  const [simResult, setSimResult] = useState<InvestSimulateResponse | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [ov, hd, rb] = await Promise.all([
        fetchInvestOverview(),
        fetchInvestHoldings(),
        fetchInvestRebalances(50),
      ]);
      setOverview(ov);
      setHoldings(hd.holdings || []);
      setRebalances(rb.rebalances || []);
    } catch (e: any) {
      setError(e?.message ?? "투자북 데이터 조회 실패");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const interval = setInterval(loadAll, 15000);
    return () => clearInterval(interval);
  }, [loadAll]);

  const runSimulation = useCallback(async () => {
    setSimLoading(true);
    setSimResult(null);
    try {
      const result = await simulateInvestRebalance();
      setSimResult(result);
    } catch (e: any) {
      setError(e?.message ?? "시뮬레이션 실패");
    } finally {
      setSimLoading(false);
    }
  }, []);

  if (loading) {
    return <div className="loading">투자북 데이터 로딩 중...</div>;
  }

  const session = overview?.session;
  const cashWeight =
    overview && overview.fx_rate != null && overview.total_equity_krw > 0
      ? (overview.cash_krw + overview.cash_usd * overview.fx_rate) /
        overview.total_equity_krw
      : null;
  const lastTurnover =
    rebalances.length > 0 ? Number(rebalances[0].turnover_bps ?? 0) : 0;

  return (
    <div>
      <p className="data-method-note">{overview?.valuation_note || "ETF 저장 계좌 · 현재 시세·당시 환율 근거가 없는 평가값은 미확인으로 표시합니다."}</p>
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>
              <Landmark size={22} color="var(--accent)" />
              <span>ETF 투자 포트폴리오 (Macro Invest)</span>
            </h2>
            <p>Toss 미국 직투 6대 매크로 ETF 자산배분 북 (SPY · QQQ · SMH · TLT · GLD · SGOV)</p>
          </div>
          <div className="btn-row">
            <button className="btn-secondary" onClick={loadAll}>
              <RefreshCw size={14} /> 새로고침
            </button>
            <button
              className="btn-primary"
              onClick={runSimulation}
              disabled={simLoading}
            >
              <PlayCircle size={15} />
              {simLoading ? "시뮬레이션 중..." : "즉시 리밸런스 시뮬레이션"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="notice-card" style={{ marginBottom: "var(--space-4)" }}>
          <ShieldAlert size={16} color="var(--red)" />
          <span>{error}</span>
        </div>
      )}

      {/* KPI Overview Grid */}
      <div className="grid grid-4" style={{ marginBottom: "var(--space-4)" }}>
        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">저장 평가액 · 입력 근거 미확인</span>
          </div>
          <div className="metric-tile-value">
            {fmtUsd(overview?.total_equity_usd)}
          </div>
          <div className="metric-tile-sub">{fmtKrw(overview?.total_equity_krw)}</div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">현금 잔액 · USD / KRW</span>
          </div>
          <div className="metric-tile-value">{fmtPct(cashWeight)}</div>
          <div className="metric-tile-sub">
            {fmtUsd(overview?.cash_usd)} · {fmtKrw(overview?.cash_krw)}
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">다음 미국 정규장 세션</span>
          </div>
          <div className="metric-tile-value">
            <span className={`badge ${session?.is_nyse_open ? "badge-green" : "badge-yellow"}`}>
              {session?.is_nyse_open ? "NYSE 개장 중" : "정규장 미개장"}
            </span>
          </div>
          <div className="metric-tile-sub">
            {session?.next_session_kst
              ? fmtKst(session.next_session_kst)
              : "—"}
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">주간 회전율 (최근)</span>
          </div>
          <div className="metric-tile-value">
            {lastTurnover > 0 ? `${lastTurnover.toFixed(0)} bps` : "0 bps"}
          </div>
          <div className="metric-tile-sub">
            {lastTurnover < 100 ? "안정적 회전율" : "회전율 집중 관리 중"}
          </div>
        </div>
      </div>

      {/* Holdings Table */}
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div className="card-title-row">
          <h3 className="card-title">6대 미국 ETF 포트폴리오 비중 현황</h3>
          <span className="card-title-meta">목표 비중 대비 ±3% 밴드 관리</span>
        </div>
        {holdings.length === 0 ? (
          <div style={{ color: "var(--text-muted)", padding: "32px 20px", textAlign: "center", fontStyle: "italic" }}>
            보유 종목이 없습니다. 리밸런스 실행 후 스냅샷이 기록됩니다.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>종목 (Ticker)</th>
                <th>자산군 / 역할</th>
                <th>보유 수량</th>
                <th>평가 금액 (KRW)</th>
                <th>현재 비중</th>
                <th>목표 비중</th>
                <th>비중 이격 (Drift)</th>
                <th>리밸런스 판정</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((h) => {
                const isHeavyDrift = Math.abs(h.drift ?? NaN) >= 0.06;
                const isMediumDrift = Math.abs(h.drift ?? NaN) >= 0.03;
                const driftColor = isHeavyDrift ? "var(--red)" : isMediumDrift ? "var(--yellow)" : "var(--green)";
                return (
                  <tr key={h.symbol}>
                    <td style={{ fontWeight: 800 }}>{h.symbol}</td>
                    <td>
                      <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{ROLE_LABELS[h.symbol] ?? h.sector}</div>
                      <div className="muted-note" style={{ margin: 0 }}>{h.name}</div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{fmtQty(h.quantity)}주</td>
                    <td style={{ fontWeight: 600 }}>{fmtKrw(h.market_value_krw)}</td>
                    <td style={{ fontWeight: 700 }}>{fmtPct(h.current_weight)}</td>
                    <td style={{ color: "var(--text-secondary)" }}>{fmtPct(h.target_weight)}</td>
                    <td
                      style={{
                        color: driftColor,
                        fontWeight: 800,
                      }}
                    >
                      {h.drift != null && h.drift >= 0 ? "+" : ""}
                      {fmtPct(h.drift)}
                    </td>
                    <td>{driftBadge(h.drift)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Simulation Result */}
      {simResult && (
        <div className="card" style={{ marginBottom: "var(--space-4)" }}>
          <div className="card-title-row">
            <BarChart3 size={16} color="var(--accent)" />
            <h3 className="card-title">리밸런스 시뮬레이션 결과 (Dry-run)</h3>
            <span className="card-title-meta">
              기준: {simResult.as_of ?? "—"} · 회전율: <strong>{simResult.turnover_bps.toFixed(0)} bps</strong> · 커브: <strong>{simResult.curve_regime}</strong>
            </span>
          </div>

          {simResult.orders.length === 0 ? (
            <div style={{ color: "var(--text-muted)", padding: "20px", textAlign: "center", fontStyle: "italic" }}>
              생성된 주문이 없습니다 (모든 종목이 리밸런스 허용 밴드 내에 위치함).
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>종목</th>
                  <th>구분</th>
                  <th>수량</th>
                  <th>참조가</th>
                  <th>원화 금액</th>
                  <th>비중 변화</th>
                  <th>집행 사유</th>
                </tr>
              </thead>
              <tbody>
                {simResult.orders.map((o, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{o.symbol}</td>
                    <td>
                      <span className={`badge ${o.side === "BUY" ? "badge-green" : "badge-red"}`}>
                        {o.side === "BUY" ? "매수 (BUY)" : "매도 (SELL)"}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{fmtQty(o.qty)}</td>
                    <td>${o.ref_price.toFixed(2)}</td>
                    <td style={{ fontWeight: 600 }}>{fmtKrw(o.notional_krw)}</td>
                    <td style={{ fontWeight: 700 }}>
                      {o.delta_weight >= 0 ? "+" : ""}
                      {fmtPct(o.delta_weight)}
                    </td>
                    <td className="muted-note" style={{ margin: 0 }}>{o.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {simResult.notes.length > 0 && (
            <div className="code-block" style={{ marginTop: "var(--space-3)", whiteSpace: "pre-wrap" }}>
              {simResult.notes.join("\n")}
            </div>
          )}
        </div>
      )}

      {/* Rebalance History */}
      <div className="card">
        <div className="card-title-row">
          <h3 className="card-title">주간 리밸런스 체결 이력</h3>
          <span className="card-title-meta">최근 50회 기록</span>
        </div>
        {rebalances.length === 0 ? (
          <div style={{ color: "var(--text-muted)", padding: "32px 20px", textAlign: "center", fontStyle: "italic" }}>
            리밸런스 실행 이력이 없습니다.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>일시</th>
                <th>실행 ID</th>
                <th>종목</th>
                <th>구분</th>
                <th>수량</th>
                <th>체결가</th>
                <th>원화 금액</th>
                <th>상태</th>
                <th>사유</th>
              </tr>
            </thead>
            <tbody>
              {rebalances.slice(0, 50).map((r, i) => (
                <tr key={i}>
                  <td style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                    {r.ts_kst
                      ? fmtKst(r.ts_kst)
                      : r.ts
                        ? fmtKst(r.ts)
                        : "—"}
                  </td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                    {r.run_id}
                  </td>
                  <td style={{ fontWeight: 700 }}>{r.symbol}</td>
                  <td>
                    {r.side && (
                      <span className={`badge ${r.side === "BUY" ? "badge-green" : "badge-red"}`}>
                        {r.side === "BUY" ? "매수" : "매도"}
                      </span>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{fmtQty(r.qty)}</td>
                  <td>
                    {r.fill_price != null
                      ? `$${Number(r.fill_price).toFixed(2)}`
                      : r.ref_price != null
                        ? `$${Number(r.ref_price).toFixed(2)}`
                        : "—"}
                  </td>
                  <td style={{ fontWeight: 600 }}>{fmtKrw(r.notional_krw)}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.status === "filled"
                          ? "badge-green"
                          : r.status === "dropped"
                            ? "badge-red"
                            : "badge-yellow"
                      }`}
                    >
                      {r.status === "filled"
                        ? "체결"
                        : r.status === "dropped"
                          ? "누락"
                          : r.status}
                    </span>
                  </td>
                  <td className="muted-note" style={{ margin: 0 }}>{r.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
